import { Component, Element, h, Host, Prop, State } from '@stencil/core';
import { DomService, StringService } from '../../services';
import { remCalc } from '../../style';
import { ButtonAppearance } from '../rcm-button/rcm-button.types';
import { Target } from '../types/link.types';
import { Alternate } from '../../../types/alternate';
import { RcmSelectCustomEvent } from '../../components';
import { UserType } from '../../../types/user-type';
import { Locale } from '../../../types/locale';

type LanguageOption = { displayName: string; targetUrl: string | null };
type LanguageOptionsMap = Record<string, LanguageOption>;

@Component({
  tag: 'rcm-language-switcher',
  styleUrl: 'rcm-language-switcher.scss',
  shadow: true,
})
export class RcmLanguageSwitcher {
  @Prop() currentLanguage: string;

  @Prop() currentCountry: string;

  @Prop({ mutable: true }) alternates: Alternate[] | string;

  @State() showModal = false;

  /**
   * We'll explicitly initialize this to an empty object.
   * Then we’ll populate it properly in `buildLanguageOptionsMap()`.
   */
  private languageOptionsMap: LanguageOptionsMap = {};

  private modalHeadline: string;

  private modalText: string;

  private modalLinkText: string;

  private modalRedirectLink: string;

  private modalCloseText: string;

  private globalContent = DomService.getGlobalContent();

  private globalConfig = DomService.getGlobalConfig();

  private currentUserType = this.globalConfig.userType;

  private currentDomain = this.globalConfig.domain;

  @Element() el: HTMLElement;

  async componentWillLoad() {
    this.currentLanguage = this.globalConfig.locale;
    this.currentCountry = this.globalConfig.country;
    this.parseAlternates();
    this.buildLanguageOptionsMap();
  }

  private parseAlternates(): void {
    if (typeof this.alternates === 'string') {
      try {
        this.alternates = JSON.parse(this.alternates) as Alternate[];
      } catch (err) {
        console.error('Failed to parse alternates:', err);
        this.alternates = [];
      }
    }
    if (!Array.isArray(this.alternates)) {
      this.alternates = [];
    }
  }

  private buildLanguageOptionsMap(): void {
    const sameDomainAlternates = (this.alternates as Alternate[]).filter((alt) => alt.domain === this.currentDomain);

    const { countryConfigs, languages } = this.globalContent;
    const localesString = countryConfigs?.[this.currentCountry]?.locales || '';
    const globallyAvailableLocales = [...new Set(localesString.split(',').filter(Boolean))];

    this.languageOptionsMap = globallyAvailableLocales.reduce<LanguageOptionsMap>((acc, locale) => {
      const displayName = languages?.[locale] || locale;
      const targetUrl = this.getTargetUrlByLocale(locale as Locale, sameDomainAlternates);

      acc[locale] = { displayName, targetUrl };
      return acc;
    }, {});
  }

  private isRetailAlternate(alternate: Alternate, targetLocale: Locale) {
    const { domain, locale } = alternate;

    return domain === this.currentDomain && locale === targetLocale;
  }

  private isInstiAlternate(alternate: Alternate, targetLocale: Locale) {
    const { domain, userType, country, locale } = alternate;

    return domain === this.currentDomain && userType === this.currentUserType && country === this.currentCountry && locale === targetLocale;
  }

  private getTargetUrlByLocale(locale: Locale, alternates: Alternate[]): string | null {
    const isRetail = this.globalConfig.userType === UserType.RETAIL;

    const lookupFunctionByDomain = isRetail ? this.isRetailAlternate.bind(this) : this.isInstiAlternate.bind(this);

    const suitableAlternate = alternates.find((a: Alternate) => lookupFunctionByDomain(a, locale));
    return suitableAlternate ? suitableAlternate.url : null;
  }

  private switchLanguage(selectEvent: RcmSelectCustomEvent<{ value: string }>) {
    if (!this.languageOptionsMap) {
      return;
    }

    const targetLocaleDisplayName = selectEvent.detail.value;
    // rcm-select accepts only display names, so we need to perform inverted search to get to the locale code
    const targetLocale = Object.keys(this.languageOptionsMap).find((locale) => {
      const languageOption = this.languageOptionsMap[locale];
      return targetLocaleDisplayName === languageOption.displayName;
    });

    if (targetLocale === this.currentLanguage) {
      return;
    }

    const languageOption = this.languageOptionsMap[targetLocale];

    if (!languageOption) {
      return;
    }

    const { targetUrl } = languageOption;

    if (!targetUrl) {
      this.displayFallbackModal(selectEvent, targetLocale);
      return;
    }

    DomService.visitSite(targetUrl);
  }

  private displayFallbackModal(e: CustomEvent, chosenLanguage: string) {
    const { pageNotAvailable } = this.globalContent;

    if (!pageNotAvailable) {
      return;
    }

    const { headline, text, homepageText, closeText } = pageNotAvailable;

    const targetLocale = chosenLanguage as Locale;

    this.modalRedirectLink = StringService.buildLocalisedHomePageLink({ locale: targetLocale });

    this.modalHeadline = headline[chosenLanguage];
    this.modalText = text[chosenLanguage];
    this.modalLinkText = homepageText[chosenLanguage];
    this.modalCloseText = closeText[chosenLanguage];
    this.showModal = true;

    const language = this.globalContent.languages?.[this.currentLanguage] || '';
    if (e?.detail?.ref) {
      e.detail.ref.setChoiceByValue(language);
    }
  }

  private getLanguageSwitcherClass(): string {
    const localeOptions = Object.keys(this.languageOptionsMap);
    return localeOptions.length <= 1 ? 'rcm-language-switcher--hide rcm-language-switcher' : 'rcm-language-switcher';
  }

  private getLanguageLabelsString(): string {
    const localeOptions = Object.keys(this.languageOptionsMap);
    const sortedLocaleNames = localeOptions
      .sort((localeA, localeB) => {
        if (localeA === this.currentLanguage) {
          return -1;
        }
        if (localeB === this.currentLanguage) {
          return 1;
        }
        return localeA.localeCompare(localeB);
      })
      .map((locale) => this.languageOptionsMap[locale].displayName);

    return sortedLocaleNames.toString();
  }

  render() {
    return (
      <Host>
        <rcm-select
          class={this.getLanguageSwitcherClass()}
          tempChoices={this.getLanguageLabelsString()}
          name="language"
          small
          onChangeEvent={(e) => this.switchLanguage(e)}
        ></rcm-select>

        <rcm-modal show={this.showModal} scrollable={false} minWidth={`${remCalc(335)}rem`} padding="0">
          <div class="modal">
            <div class="modal__icon">
              <rcm-icon
                icon="x"
                color="#373737"
                onClick={() => {
                  this.showModal = false;
                }}
                height={`${remCalc(24)}rem`}
                width={`${remCalc(24)}rem`}
              />
            </div>
            <div class="modal__textarea">
              <div class="modal__textarea__headline">{this.modalHeadline}</div>
              <div class="modal__textarea__text">{this.modalText}</div>
            </div>
            <div class="modal__buttons">
              {this.modalRedirectLink && (
                <rcm-button
                  text={this.modalLinkText}
                  appearance={ButtonAppearance.PRIMARY_YELLOW}
                  link={{ src: this.modalRedirectLink, target: Target.SELF }}
                  parentComponent="rcm-language-switcher"
                ></rcm-button>
              )}
              <rcm-button
                onRcmButtonClick={() => {
                  this.showModal = false;
                }}
                text={this.modalCloseText}
                appearance={ButtonAppearance.SECONDARY_GREY}
                parentComponent="rcm-language-switcher"
              ></rcm-button>
            </div>
          </div>
        </rcm-modal>
      </Host>
    );
  }
}
